import React from 'react';
import { AraTableRow, fetchAraData, fetchAraYears, fetchSubordinates, fetchDivisions, Subordinate, Division, BillDetailRow, fetchBillDetails, BillSortKey, PaymentTypeValue } from './api';
import AraTable from './components/AraTable';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import AraDropdowns from "./components/AraDropdowns";
import BillDetailsTable from './components/BillDetails';

const SORT_COLUMN_MAP: Record<string, string> = {
    Customer: 'customer',
    CreditPeriod: 'credit_period',
    TotalOutstanding: 'total_outstanding',
    NotDue: 'not_due',
    TotalOverdue: 'total_overdue',
    Bucket1_30: 'bucket_1_30',
    Bucket31_60: 'bucket_31_60',
    Bucket61_90: 'bucket_61_90',
    Bucket91_120: 'bucket_91_120',
    Bucket121_180: 'bucket_121_180',
    BucketOver180: 'bucket_over_180',
    Legal: 'legal',
    UnappliedReceipts: 'unapplied_receipts',
    NetOutstanding: 'net_outstanding',
};

const AraPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const [search, setSearch] = React.useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');

    const [yearMonthMap, setYearMonthMap] = React.useState<Record<string, number[]>>({});
    const [years, setYears] = React.useState<string[]>([]);
    const [availableMonths, setAvailableMonths] = React.useState<number[]>([]);
    const [year, setYear] = React.useState<string>('');
    const [month, setMonth] = React.useState<number | null>(null);

    const [divisions, setDivisions] = React.useState<Division[]>([]);
    const [selectedDivision, setSelectedDivision] = React.useState<string>('');

    const [subordinates, setSubordinates] = React.useState<Subordinate[]>([]);
    const [selectedEcode, setSelectedEcode] = React.useState<string>('');

    const [paymentType, setPaymentType] = React.useState<PaymentTypeValue>('');

    const [selectedCustomer, setSelectedCustomer] = React.useState<string | null>(null);
    const [billDetails, setBillDetails] = React.useState<BillDetailRow[]>([]);
    const [billLoading, setBillLoading] = React.useState(false);

    const [tableData, setTableData] = React.useState<AraTableRow[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [conversionType, setConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('Lakhs');

    // Pagination
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);
    const [totalItems, setTotalItems] = React.useState(0);

    const [billPage, setBillPage] = React.useState(1);
    const [billPageSize, setBillPageSize] = React.useState(20);
    const [billTotalItems, setBillTotalItems] = React.useState(0);

    // Sorting
    const [sortBy, setSortBy] = React.useState<keyof AraTableRow>('TotalOutstanding');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

    const [billSortBy, setBillSortBy] = React.useState<BillSortKey>('due_date');
    const [billSortOrder, setBillSortOrder] = React.useState<'asc' | 'desc'>('asc');

    const hasLoadedOnce = React.useRef(false);

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Reports', href: '/reports' },
            { label: 'Accounts Receivable Ageing' },
        ]);
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search.trim());
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [search]);

    React.useEffect(() => {
        const loadDivisions = async () => {
            try {
                const resp = await fetchDivisions();
                setDivisions(resp.items);
                if (resp.items.length === 1) {
                    setSelectedDivision(resp.items[0].ou_code);
                }
            } catch {
                setDivisions([]);
            }
        };
        loadDivisions();
    }, []);

    React.useEffect(() => {
        const loadSubordinates = async () => {
            try {
                const resp = await fetchSubordinates(selectedDivision || undefined);
                setSubordinates(resp.items);
                // Don't auto-select ecode anymore - it's optional
                setSelectedEcode('');
            } catch {
                setSubordinates([]);
            }
        };
        loadSubordinates();
    }, [selectedDivision]);

    React.useEffect(() => {
        const loadYears = async () => {
            try {
                const resp = await fetchAraYears();
                const map: Record<string, number[]> = {};
                const yearList: string[] = [];

                resp.years.forEach(({ year, months }) => {
                    const sortedMonths = [...months].sort((a, b) => b - a);
                    map[year] = sortedMonths;
                    yearList.push(year);
                });
                setYearMonthMap(map);
                setYears(yearList);

                if (yearList.length > 0) {
                    const defaultYear = yearList[0];
                    const defaultMonths = map[defaultYear] ?? [];

                    if (defaultMonths.length > 0) {
                        setYear(defaultYear);
                        setAvailableMonths(defaultMonths);
                        setMonth(defaultMonths[0]);
                        setPage(1);
                    }
                }
            } catch {
                setYearMonthMap({});
                setYears([]);
            }
        };
        loadYears();
    }, []);

    React.useEffect(() => {
        if (!year || !month) return;

        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const resp = await fetchAraData({
                    year,
                    month,
                    ecode: selectedEcode || undefined,
                    division: selectedDivision || undefined,
                    payment_type: paymentType || undefined,
                    page,
                    per_page: pageSize,
                    sort_by: SORT_COLUMN_MAP[sortBy],
                    sort_order: sortOrder,
                    search: debouncedSearch || undefined,
                });

                setTableData(resp?.rows ?? []);
                setTotalItems(resp?.total_items ?? 0);
            } catch (err: any) {
                setError(
                    err?.status === 403
                        ? 'You are not allowed to access this resource.'
                        : 'Failed to fetch ageing data'
                );
            } finally {
                setLoading(false);
                hasLoadedOnce.current = true;
            }
        };
        load();
    }, [year, month, selectedEcode, selectedDivision, paymentType, page, pageSize, sortBy, sortOrder, debouncedSearch]);

    React.useEffect(() => {
        if (!selectedCustomer || !year || !month) return;

        const loadBills = async () => {
            setBillLoading(true);
            try {
                const resp = await fetchBillDetails({
                    customer_name: selectedCustomer,
                    year,
                    month,
                    ecode: selectedEcode || undefined,
                    division: selectedDivision || undefined,
                    page: billPage,
                    per_page: billPageSize,
                    sort_by: billSortBy,
                    sort_order: billSortOrder,
                });

                setBillDetails(resp.items);
                setBillTotalItems(resp.total_items);
            } finally {
                setBillLoading(false);
            }
        };

        loadBills();
    }, [selectedCustomer, billPage, billPageSize, billSortBy, billSortOrder]);


    const handleSortChange = (column: keyof AraTableRow) => {
        setPage(1);
        if (sortBy === column) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    const handleCustomerClick = async (customerName: string) => {
        if (!year || !month) return;

        setSelectedCustomer(customerName);
        setBillPage(1);
        setBillLoading(true);

        try {
            const resp = await fetchBillDetails({
                customer_name: customerName,
                year,
                month,
                ecode: selectedEcode || undefined,
                division: selectedDivision || undefined,
                page: 1,
                per_page: billPageSize,
                sort_by: billSortBy,
                sort_order: billSortOrder,
            });

            setBillDetails(resp.items);
            setBillTotalItems(resp.total_items);
        } catch {
            setBillDetails([]);
            setBillTotalItems(0);
        } finally {
            setBillLoading(false);
        }
    };

    const handlePageSizeChange = (size: number) => {
        setPage(1);
        setPageSize(size);
    };

    const handleYearChange = (y: string) => {
        if (y === year) return;

        setYear(y);
        setMonth(null);
        setPage(1);
        setAvailableMonths(yearMonthMap[y] ?? []);
    };

    const handleMonthChange = (m: number) => {
        setMonth(m);
        setPage(1);
    };

    const handleDivisionChange = (divisionOuCode: string) => {
        setSelectedDivision(divisionOuCode);
        setSelectedEcode('');
        setPage(1);
    };

    const handleEcodeChange = (ecode: string) => {
        setSelectedEcode(ecode);
        setPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const handleConversionChange = React.useCallback((type: 'INR' | 'Lakhs' | 'Crores') => {
        setConversionType(type);
    }, []);

    const handleBillSortChange = (col: BillSortKey) => {
        if (billSortBy === col) {
            setBillSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setBillSortBy(col);
            setBillSortOrder('asc');
        }

        setBillPage(1);
    };

    const handleBillPageChange = (page: number) => {
        setBillPage(page);
    };

    const handleBillPageSizeChange = (size: number) => {
        setBillPage(1);
        setBillPageSize(size);
    };

    const handleBillBack = () => {
        setSelectedCustomer(null);
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden">
            <div className="flex-1 min-h-0 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
                {selectedCustomer ? (
                    <div className="flex flex-col h-full min-h-0">
                            <BillDetailsTable
                                data={billDetails}
                                loading={billLoading}
                                customerName={selectedCustomer}
                                page={billPage}
                                pageSize={billPageSize}
                                totalItems={billTotalItems}
                                sortBy={billSortBy}
                                sortOrder={billSortOrder}
                                onSortChange={handleBillSortChange}
                                onPageChange={handleBillPageChange}
                                onPageSizeChange={handleBillPageSizeChange}
                                onBack={handleBillBack}
                                conversionType={conversionType}
                                onConversionChange={setConversionType}
                            />
                    </div>
                ) : (
                    <div className="flex flex-col h-full min-h-0 gap-6">
                        <AraDropdowns
                            years={years}
                            year={year}
                            month={month}
                            months={availableMonths}
                            divisions={divisions}
                            selectedDivision={selectedDivision}
                            subordinates={subordinates}
                            selectedEcode={selectedEcode}
                            search={search}
                            paymentType={paymentType}
                            onPaymentTypeChange={(val) => {
                                setPaymentType(val);
                                setPage(1);
                            }}
                            onSearchChange={handleSearchChange}
                            onYearChange={handleYearChange}
                            onMonthChange={handleMonthChange}
                            onDivisionChange={handleDivisionChange}
                            onEcodeChange={handleEcodeChange}
                        />

                        <div className="flex-1 min-h-0">
                            <AraTable
                                data={tableData}
                                loading={loading}
                                page={page}
                                pageSize={pageSize}
                                totalItems={totalItems}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSortChange={handleSortChange}
                                onPageChange={setPage}
                                onPageSizeChange={handlePageSizeChange}
                                hasLoadedOnce={hasLoadedOnce.current}
                                hasFiltersSelected={Boolean(year && month)}
                                error={error}
                                conversionType={conversionType}
                                onConversionChange={handleConversionChange}
                                onCustomerClick={handleCustomerClick}
                            />
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{error}</p>
                </div>
            )}
        </div>
    );
};

export default AraPage;
